import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, MapPin, Wifi, UtensilsCrossed, BadgeCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { getShortTermSearch } from "../../api/services/hostService";
import { IShortTermProperty } from "../../app/model/guestModel";
import { genderTypeConfig } from "../PropertyCard/PropertyCard";

const CITIES = ["Bangalore", "Hyderabad", "Mumbai", "Pune", "Chennai", "Delhi", "Coimbatore"];

const FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?q=80&w=500",
  "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?q=80&w=500",
  "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?q=80&w=500",
  "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?q=80&w=500",
];

function toDateString(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getPrice(property: IShortTermProperty): string | null {
  if (property.vacant_beds?.length) return property.vacant_beds[0].price_per_bed;
  if (property.vacant_rooms?.length) return property.vacant_rooms[0].price_per_room;
  return null;
}

function getLocation(property: IShortTermProperty): string {
  const { locality, city } = property.location ?? {};
  if (locality && city) return `${locality}, ${city}`;
  return city ?? locality ?? "";
}

function getBadge(index: number, rating: number | string | null | undefined): string | null {
  if (Number(rating) >= 4.8) return "Top Rated";
  if (index === 0) return "Popular";
  if (index === 1) return "New";
  return null;
}

const SkeletonCard = () => (
  <div className="bg-card rounded-2xl overflow-hidden border border-border animate-pulse">
    <div className="h-48 bg-muted" />
    <div className="p-4 space-y-3">
      <div className="h-5 bg-muted rounded w-3/4" />
      <div className="h-4 bg-muted rounded w-1/2" />
      <div className="h-4 bg-muted rounded w-1/3" />
      <div className="flex justify-between items-end">
        <div className="h-8 bg-muted rounded w-20" />
        <div className="h-8 bg-muted rounded w-24" />
      </div>
    </div>
  </div>
);

const AvailableBedsSection = () => {
  const [activeCity, setActiveCity] = useState("Bangalore");
  const [properties, setProperties] = useState<IShortTermProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");

  const fetchCity = useCallback((city: string, cin: string, cout: string) => {
    setLoading(true);
    getShortTermSearch({
      keyword: city.toLowerCase(),
      checkin: cin,
      checkout: cout,
      accommodation_type: "short_bed",
    })
      .then((data: any) => {
        const arr = Array.isArray(data) ? data : (data?.results ?? data?.data ?? data?.properties ?? []);
        setProperties(arr.slice(0, 4));
      })
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const cin = toDateString(today);
    const cout = toDateString(tomorrow);
    setCheckin(cin);
    setCheckout(cout);
    fetchCity("Bangalore", cin, cout);
  }, [fetchCity]);

  const handleCityChange = (city: string) => {
    setActiveCity(city);
    fetchCity(city, checkin, checkout);
  };

  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                Available Beds This Weekend
              </h2>
              <p className="text-muted-foreground text-lg">in {activeCity}</p>
            </div>
            <Link to={`/guest-search-results?keyword=${activeCity.toLowerCase()}&checkin=${checkin}&checkout=${checkout}&accommodation_type=short_bed`}>
              <Button variant="outline" className="rounded-full w-fit">
                View All in {activeCity}
              </Button>
            </Link>
          </div>

          {/* City pills */}
          <div className="flex flex-wrap gap-2">
            {CITIES.map((city) => (
              <button
                key={city}
                onClick={() => handleCityChange(city)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                  activeCity === city
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground/70 border-border hover:border-primary/50 hover:text-foreground"
                }`}
              >
                {city}
              </button>
            ))}
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeCity}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {loading
              ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
              : properties.map((property, index) => {
                  const price = getPrice(property);
                  const primaryImg = property.images?.find(img => img.is_primary)?.image;
                  const image = primaryImg || property.images?.[0]?.image || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];
                  const badge = getBadge(index, property.average_rating);
                  const gender = property.property_gender_type ? genderTypeConfig[property.property_gender_type.toLowerCase()] : undefined;

                  return (
                    <motion.div
                      key={property.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                      whileHover={{ y: -8 }}
                      className="group cursor-pointer"
                    >
                      <Link
                        to={`/property/${property.id}?checkin=${checkin}&checkout=${checkout}&accommodation_type=short_bed`}
                        className="block h-full"
                      >
                        <div className="bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/30 hover:shadow-xl transition-all duration-300 h-full">
                          {/* Image */}
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={image}
                              alt={property.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                  FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];
                              }}
                            />
                            {badge && (
                              <Badge className="absolute top-3 left-3 bg-secondary text-secondary-foreground">
                                {badge}
                              </Badge>
                            )}
                            <div className="absolute top-3 right-3 bg-card/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                              <Star className="w-4 h-4 fill-secondary text-secondary" />
                              <span className="text-sm font-medium">
                                {property.average_rating != null
                                  ? Number(property.average_rating).toFixed(1)
                                  : "—"}
                              </span>
                              {property.total_reviews != null && (
                                <span className="text-xs text-muted-foreground">
                                  ({property.total_reviews})
                                </span>
                              )}
                            </div>
                            {gender && (
                              <div
                                className={`absolute bottom-3 right-3 backdrop-blur-sm rounded-lg px-2 py-1 text-xs flex items-center gap-1 shadow-md ${gender.className}`}
                                title={`${gender.label} property`}
                              >
                                <gender.icon size={12} />
                                {gender.label}
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="p-4">
                            <div className="flex items-center gap-1 mb-1 min-w-0">
                              <h3 className="text-lg font-semibold text-card-foreground line-clamp-1">
                                {property.name}
                              </h3>
                              {property.is_verified && (
                                <span title="Verified property" className="flex-shrink-0 flex items-center gap-0.5 text-green-600 text-xs font-medium">
                                  <BadgeCheck size={16} className="fill-green-100" />
                                  Verified
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-muted-foreground text-sm mb-3">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="line-clamp-1">{getLocation(property)}</span>
                            </div>

                            {/* Amenities */}
                            <div className="flex gap-3 mb-4">
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Wifi className="w-3 h-3" />
                                WiFi
                              </div>
                              {property.food_available && (
                                <div className="flex items-center gap-1 text-xs px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full border border-orange-100">
                                  <UtensilsCrossed className="w-3 h-3" />
                                  Food included
                                </div>
                              )}
                            </div>

                            {/* Price */}
                            <div className="flex items-end justify-between">
                              <div>
                                {price ? (
                                  <>
                                    <span className="text-2xl font-bold text-primary">
                                      ₹{price}
                                    </span>
                                    <span className="text-muted-foreground text-sm">/night</span>
                                  </>
                                ) : (
                                  <span className="text-sm text-muted-foreground">Price on request</span>
                                )}
                              </div>
                              <Button size="sm" className="rounded-full">
                                Book Now
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
          </motion.div>
        </AnimatePresence>

        {!loading && properties.length === 0 && (
          <p className="text-center text-muted-foreground mt-8">
            No beds available in {activeCity} right now. Try another city!
          </p>
        )}
      </div>
    </section>
  );
};

export default AvailableBedsSection;
